import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LockIcon from '@mui/icons-material/Lock';
import PhoneIcon from '@mui/icons-material/Phone';
import StarIcon from '@mui/icons-material/Star';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Box, Button, Container, Grid, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import './LetsDiscussSection.css';

const ACCENT = '#ef6a1d';
const ACCENT_HOVER = '#d85a10';
const NAVY = '#0b2447';
const NAVY_DEEP = '#061a36';

const WHATSAPP_URL = 'https://wa.me/+919213647646';
const PHONE_DISPLAY = '+91 92136 47646';

const trustItems = [
  { Icon: AccessTimeIcon, label: 'Avg response in 5 minutes' },
  { Icon: LockIcon, label: '100% confidential' },
  { Icon: StarIcon, label: 'No-obligation consultation' },
];

const LetsDiscussSection = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        py: { xs: 8, md: 11 },
        overflow: 'hidden',
        color: '#fff',
        background: `linear-gradient(135deg, ${NAVY_DEEP} 0%, ${NAVY} 50%, #123a73 100%)`,
        /* Grid pattern overlay */
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '52px 52px',
          maskImage:
            'radial-gradient(ellipse at center, #000 30%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, #000 30%, transparent 80%)',
          pointerEvents: 'none',
        },
        /* Orange glow top-right */
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -200,
          right: -150,
          width: 520,
          height: 520,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(239,106,29,0.28) 0%, rgba(239,106,29,0) 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      {/* Soft blue glow bottom-left */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -180,
          left: -150,
          width: 480,
          height: 480,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(59,130,246,0.22) 0%, rgba(59,130,246,0) 70%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 5, md: 6 }} alignItems="center">
          {/* Left: headline + trust row */}
          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6 }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.8,
                  px: 1.6,
                  py: 0.6,
                  mb: 3,
                  borderRadius: 5,
                  background: 'rgba(239,106,29,0.14)',
                  border: '1px solid rgba(239,106,29,0.4)',
                  color: ACCENT,
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  letterSpacing: 0.6,
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: '0.95rem' }} />
                FREE CONSULTATION
              </Box>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: 0.08 }}
            >
              <Box
                component="h2"
                sx={{
                  m: 0,
                  fontWeight: 800,
                  lineHeight: 1.15,
                  letterSpacing: -0.5,
                  fontSize: { xs: '1.9rem', sm: '2.4rem', md: '3rem' },
                  mb: 2.5,
                }}
              >
                Let&apos;s discuss how we can{' '}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(90deg, ${ACCENT} 0%, #f59e0b 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    whiteSpace: 'nowrap',
                  }}
                >
                  grow your portfolio
                </Box>{' '}
                together.
              </Box>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: 0.16 }}
            >
              <Box
                sx={{
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  color: '#cbd5e1',
                  lineHeight: 1.7,
                  mb: 4,
                  maxWidth: 560,
                }}
              >
                Speak directly with a SEBI-registered research analyst. We&apos;ll
                review your goals, risk profile and build a clear roadmap for
                your next trades — no commitments, no pressure.
              </Box>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: 0.24 }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1.4, sm: 3 }}
                sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1.4 }}
              >
                {trustItems.map(({ Icon, label }) => (
                  <Stack
                    key={label}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: 'rgba(239,106,29,0.12)',
                        color: ACCENT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid rgba(239,106,29,0.25)',
                        '& svg': { fontSize: '0.95rem' },
                      }}
                    >
                      <Icon />
                    </Box>
                    <Box
                      sx={{
                        fontSize: '0.88rem',
                        color: '#cbd5e1',
                        fontWeight: 500,
                      }}
                    >
                      {label}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </motion.div>
          </Grid>

          {/* Right: glass CTA card */}
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  p: { xs: 3.5, md: 4 },
                  background:
                    'linear-gradient(160deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
                  backdropFilter: 'blur(18px)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  boxShadow:
                    '0 30px 60px -15px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
                  overflow: 'hidden',
                  /* Subtle corner glow */
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -60,
                    right: -60,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background:
                      'radial-gradient(circle, rgba(239,106,29,0.35) 0%, rgba(239,106,29,0) 70%)',
                    pointerEvents: 'none',
                  },
                }}
              >
                {/* Floating WhatsApp icon */}
                <motion.div
                  animate={{ y: [0, -6, 0] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  style={{ display: 'inline-block', marginBottom: 16 }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 3,
                      background:
                        'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      boxShadow: '0 14px 32px rgba(37,211,102,0.4)',
                      '& svg': { fontSize: '2.2rem' },
                    }}
                  >
                    <WhatsAppIcon />
                  </Box>
                </motion.div>

                <Box
                  sx={{
                    fontSize: { xs: '1.4rem', md: '1.55rem' },
                    fontWeight: 800,
                    color: '#fff',
                    mb: 0.5,
                    letterSpacing: -0.2,
                  }}
                >
                  Talk to an Expert
                </Box>
                <Box
                  sx={{
                    fontSize: '0.93rem',
                    color: '#cbd5e1',
                    mb: 3,
                    lineHeight: 1.6,
                  }}
                >
                  Hop on a quick call or message us on WhatsApp. We&apos;re live
                  during market hours.
                </Box>

                {/* Phone row */}
                <Box
                  component="a"
                  href={`tel:${PHONE_DISPLAY.replace(/\s/g, '')}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.6,
                    mb: 2.5,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    textDecoration: 'none',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.08)',
                      borderColor: 'rgba(239,106,29,0.4)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 1.5,
                      background: 'rgba(239,106,29,0.12)',
                      color: ACCENT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid rgba(239,106,29,0.25)',
                      flexShrink: 0,
                      '& svg': { fontSize: '1.1rem' },
                    }}
                  >
                    <PhoneIcon />
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        fontSize: '0.72rem',
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        fontWeight: 600,
                      }}
                    >
                      Call us directly
                    </Box>
                    <Box
                      sx={{
                        fontSize: '1.02rem',
                        fontWeight: 700,
                        color: '#fff',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {PHONE_DISPLAY}
                    </Box>
                  </Box>
                </Box>

                {/* Primary CTA */}
                <Button
                  fullWidth
                  size="large"
                  component="a"
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: ACCENT,
                    color: '#fff',
                    fontWeight: 700,
                    textTransform: 'none',
                    py: 1.4,
                    fontSize: '1rem',
                    borderRadius: 2,
                    boxShadow: '0 12px 28px rgba(239,106,29,0.45)',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      background: ACCENT_HOVER,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 16px 32px rgba(239,106,29,0.55)',
                    },
                  }}
                >
                  Get In Touch on WhatsApp
                </Button>

                {/* Tiny disclaimer */}
                <Box
                  sx={{
                    mt: 2.5,
                    fontSize: '0.78rem',
                    color: '#94a3b8',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.7,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: '#22c55e',
                      boxShadow: '0 0 0 4px rgba(34,197,94,0.18)',
                    }}
                  />
                  Available Mon – Sat · 9 AM – 6 PM
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LetsDiscussSection;
